import React, { useEffect, useMemo, useRef } from 'react';
import { Row } from 'react-table';
import { DataTablePresenter, ITableColumn } from '../../data-table';
import * as S from './statement-data-table.styles';

export type StatementDataTableAlign = 'left' | 'right';

export interface StatementDataTableColumn<T> {
    id: string;
    header: React.ReactNode;
    /** Pixel width used by DataTable flex layout. Larger values take more remaining space. */
    width?: number;
    align?: StatementDataTableAlign;
    render: (row: T) => React.ReactNode;
}

export interface StatementDataTableProps<T extends { id?: string }> {
    name?: string;
    columns: StatementDataTableColumn<T>[];
    rows: T[];
    emptyMessage?: string;
    onRowClick?: (row: T) => void;
    isRowClickable?: (row: T) => boolean;
    getRowHref?: (row: T) => string;
}

export function StatementDataTable<T extends { id?: string }>(
    props: StatementDataTableProps<T>,
): React.ReactElement {
    const {
        name = 'statement-data-table',
        columns,
        rows,
        emptyMessage = 'No data found',
        onRowClick,
        isRowClickable,
        getRowHref,
    } = props;

    const tableColumns = useMemo(
        (): ITableColumn<T>[] =>
            columns.map(column => {
                const width = column.width ?? 120;
                return {
                    id: column.id,
                    Header: () => <S.HeaderCell align={column.align}>{column.header}</S.HeaderCell>,
                    accessor: 'id' as keyof T,
                    disableSortBy: true,
                    minWidth: width,
                    width,
                    Cell: ({ row }: { row: Row<T> }) => (
                        <S.BodyCell align={column.align}>{column.render(row.original)}</S.BodyCell>
                    ),
                };
            }),
        [columns],
    );

    const presenter = useRef(
        new DataTablePresenter<T>({
            name,
            data: [],
            columns: tableColumns,
            filterSchemas: [],
        }),
    ).current;

    useEffect(() => {
        presenter.onColumnsUpdate(tableColumns);
    }, [presenter, tableColumns]);

    useEffect(() => {
        presenter.onDataUpdate(rows);
        presenter.onDataTotalChange(rows.length);
    }, [presenter, rows]);

    if (rows.length === 0) {
        return <S.Empty>{emptyMessage}</S.Empty>;
    }

    return (
        <S.TableCard>
            <S.StyledDataTable
                presenter={presenter}
                hideFiltering={true}
                hidePagination={true}
                hideNavigation={true}
                hideSettings={true}
                disableSorting={true}
                onRowClick={
                    onRowClick
                        ? row => {
                              if (!isRowClickable || isRowClickable(row.original)) {
                                  onRowClick(row.original);
                              }
                          }
                        : undefined
                }
                getRowHref={
                    getRowHref ? row => getRowHref(row.original) : undefined
                }
            />
        </S.TableCard>
    );
}
